import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import { isEmpty } from "lodash";
import { apiPost } from "../../utils/apiFetch";
import apiPath from "../../utils/pathObj";
import { BsRobot, BsPersonCheck } from "react-icons/bs";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

function getMatchBadgeColor(score) {
  if (score >= 80) return "bg-green-100 text-green-800";
  if (score >= 60) return "bg-yellow-100 text-yellow-800";
  if (score >= 40) return "bg-orange-100 text-orange-800";
  return "bg-red-100 text-red-800";
}

function formatLocation(location) {
  const parts = [];
  if (location?.city) parts.push(location.city);
  if (location?.state) parts.push(location.state);
  if (location?.country) parts.push(location.country);
  return parts.join(", ") || "Not specified";
}

// ── Fully deterministic, computed from data already on screen —
// no AI call, so it's instant and can never invent anything. ──
function buildExplanation(candidate) {
  const name = candidate.employee.name;
  const score = candidate.matchScore;
  const matchedCount = candidate.details?.matchedCount ?? candidate.matchedSkills?.length ?? 0;
  const totalSkills = candidate.details?.totalSkills ?? 0;
  const experience = candidate.employee?.parsedResumeData?.experience || 0;
  const missing = candidate.missingSkills || [];
  const locationMatched = (candidate.locationMatch?.matchPercentage || 0) > 0;

  let text = `${name} has a ${score}% match because ${matchedCount} of the ${totalSkills} required skills are present in the profile. `;
  text += `The candidate has ${experience} year${experience === 1 ? "" : "s"} of experience. `;
  text += missing.length > 0
    ? `Missing skills: ${missing.join(", ")}. `
    : `No required skills are missing. `;
  text += locationMatched ? `Location matches the job requirement.` : `Location does not match the job requirement.`;

  return text;
}

function buildComparisonNarrative(candidates) {
  const sorted = [...candidates].sort((a, b) => b.matchScore - a.matchScore);
  const leader = sorted[0];
  const rest = sorted.slice(1);

  if (rest.length === 0) return "";

  const comparisons = rest.map((c) => {
    const leaderSkills = leader.details?.matchedCount ?? leader.matchedSkills?.length ?? 0;
    const cSkills = c.details?.matchedCount ?? c.matchedSkills?.length ?? 0;
    return `${leader.employee.name} leads ${c.employee.name} with a ${leader.matchScore}% match (${leaderSkills}/${leader.details?.totalSkills || 0} skills) versus ${c.matchScore}% (${cSkills}/${c.details?.totalSkills || 0} skills).`;
  });

  return comparisons.join(" ");
}

function CandidateAiDrawer({ drawerState, onClose }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { mode, candidates, jobGroup } = drawerState;

  const [selectingFor, setSelectingFor] = useState(null);
  const [interviewPopup, setInterviewPopup] = useState(null);
  const [error, setError] = useState(null);

  const handleSelectForInterview = async (candidate) => {
    const employeeID = candidate.employee._id;
    try {
      setSelectingFor(employeeID);
      setError(null);
      const result = await apiPost(apiPath.selectCandidateForInterview, {
        employeeID,
        jobID: candidate.jobId
      });
      if (result?.status === 200 && result?.data?.success) {
        setInterviewPopup({ name: candidate.employee.name, jobName: candidate.jobName || jobGroup?.jobName });
      } else {
        setError(t("ACTION_FAILED"));
      }
    } catch (err) {
      console.error("Select for interview error:", err);
      setError(t("ACTION_FAILED"));
    } finally {
      setSelectingFor(null);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black bg-opacity-30" onClick={onClose} />
      <div className="fixed top-0 right-0 z-50 h-full w-full max-w-xl bg-white shadow-2xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-[#E3E3E3] px-6 py-4 flex items-center justify-between z-10">
          <h3 className="text-lg font-semibold text-gray-900">
            {mode === "compare" ? t("COMPARE_CANDIDATES") : candidates[0]?.employee?.name}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-[#B8BBBF] hover:bg-gray-100 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="p-6 space-y-6">
          {error && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          {mode === "compare" && (
            <div>
              <div className="overflow-x-auto rounded-lg border border-[#E9EDF9] mb-4">
                <table className="w-full text-xs text-left">
                  <thead className="bg-[#E1E6EE] text-gray-900">
                    <tr>
                      <th className="py-2 px-3">{t("EMPLOYEE_NAME")}</th>
                      <th className="py-2 px-3">{t("MATCH_SCORE")}</th>
                      <th className="py-2 px-3">{t("SKILLS_MATCH")}</th>
                      <th className="py-2 px-3">{t("EXPERIENCE")}</th>
                      <th className="py-2 px-3">{t("LOCATION")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidates.map((c) => (
                      <tr key={c.employee._id} className="border-t bg-white">
                        <td className="py-2 px-3 font-medium">{c.employee.name}</td>
                        <td className="py-2 px-3">
                          <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${getMatchBadgeColor(c.matchScore)}`}>
                            {c.matchScore}%
                          </span>
                        </td>
                        <td className="py-2 px-3">{c.details?.matchedCount || 0}/{c.details?.totalSkills || 0}</td>
                        <td className="py-2 px-3">{c.employee?.parsedResumeData?.experience || 0} {t("YEARS")}</td>
                        <td className="py-2 px-3">{formatLocation(c.employee?.location)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="p-3 bg-[#F3F5FA] rounded-lg text-sm text-gray-700 flex gap-2">
                <BsRobot className="w-4 h-4 text-LightBlue flex-shrink-0 mt-0.5" />
                <p>{buildComparisonNarrative(candidates)}</p>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-3">
                {candidates.map((c) => (
                  <div key={c.employee._id} className="border border-gray-200 rounded-lg p-3">
                    <p className="text-sm font-medium mb-1">{c.employee.name}</p>
                    <div className="flex flex-wrap gap-1 mb-1">
                      {c.matchedSkills.map((s, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-green-100 text-green-800 rounded-full text-[11px]">{s}</span>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {c.missingSkills.map((s, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-red-100 text-red-800 rounded-full text-[11px]">{s}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {mode === "details" && candidates.map((candidate) => {
            const employee = candidate.employee;
            const jobDetails = candidate.job || jobGroup?.jobDetails;
            const employeeID = employee._id;

            return (
              <div key={employeeID} className="space-y-6">
                <div className="p-4 bg-gray-50 rounded-lg flex items-center justify-between">
                  <div>
                    <h4 className="text-base font-semibold">{jobDetails?.name}</h4>
                    <p className="text-gray-600 text-sm">{t("RECOMMENDED_CANDIDATE")}</p>
                  </div>
                  <div className={`px-3 py-1.5 rounded-full ${getMatchBadgeColor(candidate.matchScore)}`}>
                    <span className="font-bold">{candidate.matchScore}%</span>
                  </div>
                </div>

                <div className="p-4 bg-[#F3F5FA] rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <BsRobot className="w-4 h-4 text-LightBlue" />
                    <span className="text-sm font-semibold text-gray-800">{t("AI_MATCH_EXPLANATION")}</span>
                  </div>
                  <p className="text-sm text-gray-700">{buildExplanation(candidate)}</p>
                </div>

                <div>
                  <h3 className="text-sm font-semibold mb-3 pb-2 border-b">{t("EMPLOYEE_INFORMATION")}</h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><strong className="block text-xs text-gray-500">{t("NAME")}</strong>{employee.name}</div>
                    <div><strong className="block text-xs text-gray-500">{t("EMAIL")}</strong>{employee.email}</div>
                    <div><strong className="block text-xs text-gray-500">{t("LOCATION")}</strong>{formatLocation(employee.location)}</div>
                    <div><strong className="block text-xs text-gray-500">{t("EXPERIENCE")}</strong>{employee.parsedResumeData?.experience || 0} {t("YEARS")}</div>
                    <div className="col-span-2"><strong className="block text-xs text-gray-500">{t("EDUCATION")}</strong>{employee.parsedResumeData?.education || t("NOT_SPECIFIED")}</div>
                    {employee.resume?.filename && (
                      <div className="col-span-2">
                        <Link
                          to={`${process.env.REACT_APP_MEDIA_URL}${employee.resume.filename}`}
                          target="_blank" rel="noopener noreferrer"
                          className="inline-block mt-1 px-3 py-1 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded text-xs"
                        >
                          {employee.resume?.originalName || t("VIEW_RESUME")}
                        </Link>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-semibold mb-3 pb-2 border-b">{t("SKILLS_ANALYSIS")}</h3>
                  <div className="mb-2">
                    <p className="text-xs font-bold text-gray-600 mb-1">{t("MATCHED_SKILLS")} ({candidate.matchedSkills?.length || 0})</p>
                    <div className="flex flex-wrap gap-1">
                      {!isEmpty(candidate.matchedSkills) ? candidate.matchedSkills.map((s, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-green-100 text-green-800 rounded-full text-xs">{s}</span>
                      )) : <span className="text-gray-500 text-xs">{t("NO_MATCHED_SKILLS")}</span>}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-600 mb-1">{t("MISSING_SKILLS")} ({candidate.missingSkills?.length || 0})</p>
                    <div className="flex flex-wrap gap-1">
                      {!isEmpty(candidate.missingSkills) ? candidate.missingSkills.map((s, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-red-100 text-red-800 rounded-full text-xs">{s}</span>
                      )) : <span className="text-gray-500 text-xs">{t("NO_MISSING_SKILLS")}</span>}
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  disabled={selectingFor === employeeID}
                  onClick={() => handleSelectForInterview(candidate)}
                  className="w-full py-2.5 rounded-lg bg-LightBlue hover:bg-DarkBlue text-white text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {selectingFor === employeeID
                    ? <AiOutlineLoading3Quarters className="animate-spin w-4 h-4" />
                    : <BsPersonCheck className="w-4 h-4" />}
                  {t("SELECT_FOR_INTERVIEW")}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {interviewPopup && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-2">{t("CANDIDATE_MOVED_TO_INTERVIEW")}</h3>
            <p className="text-gray-600 mb-6">
              {interviewPopup.name} {t("HAS_BEEN_MOVED_TO_INTERVIEW_FOR")}{" "}
              <strong>{interviewPopup.jobName}</strong>. {t("PLEASE_SCHEDULE_THEIR_INTERVIEW_NEXT")}
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setInterviewPopup(null)}
                className="px-4 py-2 rounded text-sm bg-[#E1E1E1] hover:bg-gray-300"
              >
                {t("O_CLOSE")}
              </button>
              <button
                type="button"
                onClick={() => navigate("/interview-manager")}
                className="px-4 py-2 rounded text-sm bg-LightBlue text-white hover:bg-DarkBlue"
              >
                {t("GO_TO_INTERVIEW_SCREEN")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default CandidateAiDrawer;